<script lang="ts" setup>
import { computed } from 'vue'

import PageContainer from '@/components/site/PageContainer.vue'
import SectionHeader from '@/components/site/SectionHeader.vue'
import StoreButtons from '@/components/site/StoreButtons.vue'
import PvIcon from '@/components/ui/PvIcon.vue'
import { footerColumns } from '@/constants/navigation'

// Declare the version constant
declare const __APP_VERSION__: string

// Make version reactive
const appVersion = computed(() => __APP_VERSION__)
const year = computed(() => new Date().getFullYear())

// currentColor brand marks (Simple Icons), since the site theme controls
// their color via hover/muted-color classes rather than a fixed fill.
const socialLinks = [
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/pinvision/',
    icon: 'instagram',
  },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/pinvision/',
    icon: 'facebook',
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/pinvision/',
    icon: 'linkedin',
  },
]
</script>

<template>
  <footer class="footer relative bg-surface-100 py-16 lg:py-24">
    <PageContainer>
      <SectionHeader :title="$t('common:footerHeading')" :gradient="false" />

      <div class="flex justify-center gap-4 sm:gap-6 mb-16 lg:mb-20">
        <a
          v-for="social in socialLinks"
          :key="social.label"
          :aria-label="social.label"
          :href="social.href"
          class="group flex flex-col items-center gap-2 text-muted-color-emphasis transition-all hover:text-primary-500"
          rel="noopener"
          target="_blank"
        >
          <span
            class="flex items-center justify-center w-12 h-12 rounded-full border border-surface-200 bg-surface-0 transition-all group-hover:-translate-y-1"
          >
            <PvIcon :name="social.icon" class="w-5 h-5" />
          </span>
          <span class="text-xs font-medium">{{ social.label }}</span>
        </a>
      </div>

      <div class="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
        <div v-for="column in footerColumns" :key="column.title ?? column.titleKey">
          <h2 class="text-sm font-semibold uppercase tracking-wider mb-4">
            {{ column.title ?? $t(column.titleKey!) }}
          </h2>
          <template v-for="item in column.items" :key="item.labelKey">
            <RouterLink v-if="item.to" :to="item.to" class="footer-link">
              {{ $t(item.labelKey) }}
            </RouterLink>
            <a v-else :href="item.href" class="footer-link" rel="noopener" target="_blank">
              {{ $t(item.labelKey) }}
            </a>
          </template>
        </div>

        <div class="col-span-2 sm:col-span-1">
          <h2 class="text-sm font-semibold uppercase tracking-wider mb-4">
            {{ $t('common:footerDownload') }}
          </h2>
          <StoreButtons class="justify-start!" />
        </div>
      </div>
    </PageContainer>

    <div class="border-t border-surface-200 mt-12 lg:mt-16">
      <PageContainer
        class="py-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-center sm:text-left"
      >
        <p class="text-xs text-muted-color-emphasis">© {{ year }} Pin Vision</p>
        <p class="text-xs text-muted-color-emphasis">v.{{ appVersion }}</p>
      </PageContainer>
    </div>
  </footer>
</template>

<style lang="scss" scoped>
@reference '../../assets/styles/root.scss';
.footer {
  @media (min-width: 1000px) {
    clip-path: ellipse(99% 100% at bottom);
  }
}

.footer-link {
  @apply text-sm text-muted-color-emphasis hover:text-color transition-colors block mb-3;
}
</style>
